import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API, useUser } from "../contexts/UserContext";
import Header from "../components/Header";
import TopicContent from "../components/TopicContent";
import Quiz from "../components/Quiz";
import { Button } from "../components/ui/button";
import { Clock, Bookmark, BookmarkCheck, ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const Module = () => {
 const { moduleId } = useParams();
 const { user, progress, refreshProgress, loading } = useUser();
 const navigate = useNavigate();
 const [module, setModule] = useState(null);
 const [topicIdx, setTopicIdx] = useState(0);

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate("/"); return; }
 axios.get(`${API}/course/modules/${moduleId}`).then((r) => {
 setModule(r.data);
 setTopicIdx(0);
 window.scrollTo(0, 0);
 }).catch(() => navigate("/home"));
 }, [moduleId, user, navigate, loading]);

 if (!module) {
 return (
 <div className="min-h-screen bg-white flex items-center justify-center text-zinc-500">
 Loading…
 </div>
 );
 }

 const topic = module.topics[topicIdx];
 const completed = new Set(progress?.completed_topics || []);
 const bookmarks = new Set(progress?.bookmarks || []);
 const isDone = completed.has(topic.id);
 const isBookmarked = bookmarks.has(topic.id);

 const goPrev = () => { if (topicIdx > 0) { setTopicIdx(topicIdx - 1); window.scrollTo(0, 0); } };
 const goNext = () => {
 if (topicIdx < module.topics.length - 1) { setTopicIdx(topicIdx + 1); window.scrollTo(0, 0); }
 else navigate("/home");
 };

 const toggleBookmark = async () => {
 await axios.post(`${API}/progress/bookmark`, { user_id: user.id, topic_id: topic.id });
 await refreshProgress();
 };

 const onQuizComplete = async () => {
 await refreshProgress();
 };

 return (
 <div className="min-h-screen bg-white ">
 <Header />
 <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
 <Link
 to="/home"
 data-testid="back-to-home-btn"
 className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-orange-600 :text-orange-400 mb-6 transition-colors"
 >
 <ArrowLeft className="w-4 h-4" />
 Back to home
 </Link>

 <div className="grid lg:grid-cols-[260px_1fr] gap-8">
 {/* Sidebar — topic list */}
 <aside className="lg:sticky lg:top-20 h-fit">
 <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
 Module {module.order}
 </div>
 <h2 className="font-heading text-xl font-bold text-zinc-900 mb-1 leading-tight">
 {module.title}
 </h2>
 <p className="text-xs text-zinc-500 mb-5">
 {Math.round(module.duration_minutes / 60)}h total · {module.topics.length} topics
 </p>
 <nav className="space-y-1">
 {module.topics.map((t, i) => {
 const done = completed.has(t.id);
 const active = i === topicIdx;
 return (
 <button
 key={t.id}
 data-testid={`sidebar-topic-${i}`}
 onClick={() => { setTopicIdx(i); window.scrollTo(0, 0); }}
 className={`w-full text-left px-3 py-2.5 rounded-sm text-sm flex items-start gap-2 transition-colors ${
 active
 ? "bg-zinc-900 text-white "
 : "text-zinc-700 hover:bg-zinc-100 :bg-zinc-900"
 }`}
 >
 <span className={`mt-0.5 text-xs font-bold ${active ? "" : "text-orange-500"}`}>
 {i + 1}.
 </span>
 <span className="flex-1">{t.title}</span>
 {done && <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${active ? "" : "text-green-500"}`} />}
 </button>
 );
 })}
 </nav>
 </aside>

 {/* Main content */}
 <div className="min-w-0 max-w-3xl">
 <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
 <div className="flex items-center gap-3">
 <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-600 ">
 Topic {topicIdx + 1} of {module.topics.length}
 </span>
 {isDone && (
 <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 ">
 <CheckCircle2 className="w-3.5 h-3.5" />
 Completed
 </span>
 )}
 </div>
 <button
 data-testid="bookmark-btn"
 onClick={toggleBookmark}
 className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-orange-600 :text-orange-400 transition-colors"
 >
 {isBookmarked ? (
 <>
 <BookmarkCheck className="w-4 h-4 fill-orange-500 text-orange-500" />
 Bookmarked
 </>
 ) : (
 <>
 <Bookmark className="w-4 h-4" />
 Bookmark
 </>
 )}
 </button>
 </div>

 <h1
 data-testid="topic-title"
 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-zinc-900 leading-tight mb-3"
 >
 {topic.title}
 </h1>

 <div className="flex items-center gap-3 mb-2">
 <span
 data-testid="topic-reading-time"
 className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-semibold"
 >
 <Clock className="w-3 h-3" />
 {topic.reading_time_min} min read
 </span>
 </div>

 <TopicContent topic={topic} />

 <Quiz key={topic.id} topic={topic} onComplete={onQuizComplete} />

 {/* Nav */}
 <div className="mt-12 flex items-center justify-between gap-4 pt-8 border-t border-zinc-200 ">
 <Button
 variant="outline"
 disabled={topicIdx === 0}
 onClick={goPrev}
 data-testid="topic-prev-btn"
 className="rounded-sm"
 >
 <ChevronLeft className="w-4 h-4 mr-1" />
 Previous
 </Button>
 <Button
 onClick={goNext}
 data-testid="topic-next-btn"
 className="rounded-sm bg-orange-500 hover:bg-orange-600 text-black font-bold"
 >
 {topicIdx === module.topics.length - 1 ? "Finish module" : "Next topic"}
 <ChevronRight className="w-4 h-4 ml-1" />
 </Button>
 </div>
 </div>
 </div>
 </main>
 </div>
 );
};

export default Module;
